import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import DeleteIcon from '@mui/icons-material/Delete'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import VisibilityIcon from '@mui/icons-material/Visibility'
import {
  Autocomplete,
  Box,
  Divider,
  IconButton,
  MenuItem,
  Radio,
  Select,
  TextField,
  Typography,
  createFilterOptions,
  useTheme,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import editIcon from '@/assets/images/EditIcon.svg'
import link from '@/assets/images/Link.svg'
import Button from '@/components/Button'
import Dialog from '@/components/Dialog'
import {
  useDeleteUserAccessMutation,
  useInviteUserMutation,
  useLazyGetInvitedUserQuery,
  useLazyGetSharedAccessUserQuery,
  useUpdateAccessMutation,
} from '@/store/apiService'

const filter = createFilterOptions<InvitedUserOption>()

interface InvitedUserOption {
  title: string
  id: string
  email?: string
  inputValue?: string
}

interface ShareProjectProps {
  open: boolean
  onClose: () => void
  initialData: {
    id: string
    patient_name: string
  }
  refetchRecipe: () => void
}

function ShareProject({
  open,
  onClose,
  initialData,
  refetchRecipe,
}: ShareProjectProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'project.shareProjectDialog',
  })

  const [invitedUserValue, setInvitedUserValue] = useState<
    InvitedUserOption | string | null
  >(null)
  const [accessDialogOpen, setAccessDialogOpen] = useState(false)
  const [selectedAccessRole, setSelectedAccessRole] = useState('viewer')
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [pendingRoleChange, setPendingRoleChange] = useState<{
    userId: string
    newRole: string
    userEmail: string
    oldRole: string
  } | null>(null)

  const theme = useTheme()
  const [triggerGetInvitedUser, { data: invitedUserList }] =
    useLazyGetInvitedUserQuery()
  const [triggerGetSharedAccessUser, { data: sharedUserList }] =
    useLazyGetSharedAccessUserQuery()
  const [updateAccess] = useUpdateAccessMutation()

  useEffect(() => {
    if (initialData?.id) {
      triggerGetInvitedUser(initialData.id)
      triggerGetSharedAccessUser(initialData.id)
    }
  }, [initialData?.id])

  const [inviteUser] = useInviteUserMutation()
  const [deleteAccess] = useDeleteUserAccessMutation()

  const invitedUserOptions: InvitedUserOption[] = Array.isArray(invitedUserList)
    ? invitedUserList.map(
        (item: { id: string; name: string; email: string }) => ({
          title: item.name,
          id: item.id,
          email: item.email,
        }),
      )
    : []

  const handleInvitedUserChange = (
    event: React.SyntheticEvent,
    newValue: InvitedUserOption | string | null,
  ) => {
    if (typeof newValue === 'string') {
      setInvitedUserValue(newValue)
    } else if (newValue?.inputValue) {
      setInvitedUserValue(newValue.inputValue)
    } else {
      setInvitedUserValue(newValue)
    }
  }

  const handleAddUser = () => {
    if (invitedUserValue) {
      setAccessDialogOpen(true)
    }
  }

  const handleRemoveUser = async (userId: string) => {
    const userToDelete = sharedUserList?.find((user: any) => user.id === userId)
    try {
      await deleteAccess({
        caseId: initialData.id,
        shared_id: userToDelete?.id,
      }).unwrap()

      toast.success(`Access has been removed.`)
      refetchRecipe()
      triggerGetSharedAccessUser(initialData.id)
    } catch (err: any) {
      console.error(err)
      toast.error(err?.data?.detail || 'Failed to remove user access.')
    }
  }

  const handleShareProject = async () => {
    if (!invitedUserValue) return

    const email = (() => {
      if (typeof invitedUserValue === 'string') {
        return invitedUserValue
      }
      if (invitedUserValue?.inputValue) {
        return invitedUserValue.inputValue
      }
      return invitedUserValue?.email || invitedUserValue.title
    })()

    try {
      await inviteUser({
        id: initialData.id,
        data: {
          email,
          role: selectedAccessRole,
        },
      }).unwrap()

      toast.success(`${t('projectShared')}`)
      refetchRecipe()
      setAccessDialogOpen(false)
      onClose()
    } catch (err: any) {
      console.error(err)
      toast.error(err?.data?.detail || `${t('failedToShare')}`)
    }
  }

  const roleOptions = [
    {
      value: 'viewer',
      label: 'Viewer',
      icon: <VisibilityIcon fontSize="medium" />,
    },
    {
      value: 'editor',
      label: 'Editor',
      icon: <img src={editIcon} alt="viewer icon" />,
    },
  ]

  return (
    <>
      <Dialog
        isOpen={open}
        onClose={onClose}
        title={
          <Typography fontSize={20} fontWeight={500}>
            {`${t('title')} ${initialData?.patient_name}`}
          </Typography>
        }
        content={
          <Box display="flex" flexDirection="column" gap={3}>
            <Box display={'flex'} flexDirection="column" gap={2}>
              <Typography fontWeight={500} fontSize={'16px'}>
                {t('people')}
              </Typography>
              <Autocomplete
                value={invitedUserValue}
                onChange={handleInvitedUserChange}
                filterOptions={(options, params) => {
                  const filtered = filter(options, params)
                  const { inputValue } = params
                  if (
                    inputValue !== '' &&
                    !options.some((option) => option.title === inputValue)
                  ) {
                    filtered.push({
                      inputValue,
                      title: `Add "${inputValue}"`,
                      id: '',
                    })
                  }
                  return filtered
                }}
                options={invitedUserOptions}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') {
                    return option
                  }
                  if (option.inputValue) {
                    return option.inputValue
                  }
                  return option.title
                }}
                renderOption={(props, option) => (
                  <li {...props} key={`${option.id}-${option.title}`}>
                    <Box>
                      <Typography fontWeight={400} fontSize={'16px'}>
                        {option.title}
                      </Typography>
                      {option.email && (
                        <Typography
                          fontSize={'14px'}
                          color="#C6C6C8"
                          fontWeight={400}
                        >
                          {option.email}
                        </Typography>
                      )}
                    </Box>
                  </li>
                )}
                freeSolo
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('placeholder')}
                    size="medium"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: '40px',
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#5B5B5B',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#90CAF9',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        top: '-8px',
                      },
                      '& .MuiInputLabel-shrink': {
                        top: '0px',
                      },
                    }}
                  />
                )}
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: 'preventOverflow',
                        options: { boundary: 'window' },
                      },
                    ],
                  },
                  paper: {
                    sx: {
                      backgroundColor: theme.palette.background.disableTab,
                      fontSize: 16,
                      fontWeight: 400,
                      paddingY: '6px',
                    },
                  },
                }}
              />
            </Box>

            <Divider />

            <Box>
              <Typography fontWeight={500} fontSize={'16px'}>
                {t('access')}
              </Typography>
              {Array.isArray(sharedUserList) && sharedUserList.length > 0 ? (
                sharedUserList.map((user: any) => (
                  <Box
                    key={user.id}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    py={1}
                  >
                    <Box>
                      <Typography fontSize={16} fontWeight={400}>
                        {user.name} ({user.email})
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Select
                        value={user.role}
                        onChange={(e) => {
                          const newRole = e.target.value
                          if (newRole === user.role) return

                          setPendingRoleChange({
                            userId: user.id,
                            newRole,
                            userEmail: user.email,
                            oldRole: user.role,
                          })
                          setConfirmDialogOpen(true)
                        }}
                        renderValue={(selected) => {
                          const selectedOption = roleOptions.find(
                            (opt) => opt.value === selected,
                          )
                          return selectedOption?.label || selected
                        }}
                        IconComponent={ExpandMoreIcon}
                        size="small"
                        variant="standard"
                        disableUnderline
                        sx={{
                          backgroundColor: 'transparent',
                          fontWeight: 400,
                          lineHeight: '20px',
                          fontSize: '14px',
                          color: '#C6C6C8',
                          minWidth: '60px',
                          '& .MuiSelect-icon': { color: '#C6C6C8' },
                          '.MuiSelect-icon': {
                            color: '#C6C6C8',
                            top: '44%',
                            transform: 'translateY(-50%)',
                          },
                        }}
                      >
                        {roleOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            <Box
                              display="flex"
                              alignItems="center"
                              gap={'12px'}
                            >
                              {option.icon}
                              {option.label}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>

                      <IconButton
                        size="small"
                        onClick={async () => handleRemoveUser(user.id)}
                      >
                        <DeleteIcon
                          fontSize="small"
                          sx={{ color: '#E46962' }}
                        />
                      </IconButton>
                    </Box>
                  </Box>
                ))
              ) : (
                <Typography
                  mt={1}
                  color="#C6C6C8"
                  fontSize={'14px'}
                  fontWeight={400}
                >
                  {t('noUser')}
                </Typography>
              )}
            </Box>
          </Box>
        }
        actionButtons={
          <Box width="100%" gap={2} display="flex" justifyContent="flex-end">
            <Button
              label={t('cancel')}
              onClick={onClose}
              variant="outlined"
              textTransform="uppercase"
            />
            <Button
              label={t('Next')}
              onClick={handleAddUser}
              variant="contained"
              endIcon={<ChevronRightIcon />}
              disabled={!invitedUserValue}
              textTransform="uppercase"
            />
          </Box>
        }
        paperStyles={{ width: '800px' }}
        triggerElement={undefined}
      />

      <Dialog
        isOpen={accessDialogOpen}
        onClose={() => setAccessDialogOpen(false)}
        title={
          <Typography fontSize={20} fontWeight={500}>
            {`${t('title')} ${initialData?.patient_name} ${t('to')} `}
            <Typography component="span" fontWeight={500} fontSize={20}>
              {typeof invitedUserValue === 'string'
                ? invitedUserValue
                : invitedUserValue?.title || ''}
            </Typography>
          </Typography>
        }
        content={
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography fontWeight={500} fontSize={16}>
              {t('accessRole')}
            </Typography>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '9px',
                cursor: 'pointer',
              }}
              onClick={() => setSelectedAccessRole('viewer')}
            >
              <Radio
                checked={selectedAccessRole === 'viewer'}
                value="viewer"
                onChange={() => {}}
                sx={{ mt: '-8px' }}
              />
              <Box gap={'4px'} display={'flex'} flexDirection={'column'}>
                <Typography fontWeight={400} fontSize={'16px'}>
                  {t('viewer')}
                </Typography>
                <Typography fontSize={14} color="#C6C6C8" fontWeight={400}>
                  {t('viewerDescription')}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '9px',
                cursor: 'pointer',
              }}
              onClick={() => setSelectedAccessRole('editor')}
            >
              <Radio
                checked={selectedAccessRole === 'editor'}
                value="editor"
                onChange={() => {}}
                sx={{ mt: '-8px' }}
              />
              <Box gap={'4px'} display={'flex'} flexDirection={'column'}>
                <Typography fontWeight={400} fontSize={'16px'}>
                  {t('editor')}
                </Typography>
                <Typography fontSize={14} color="#C6C6C8" fontWeight={400}>
                  {t('editorDescription')}
                </Typography>
              </Box>
            </Box>
          </Box>
        }
        actionButtons={
          <Box width="100%" display="flex" justifyContent="space-between">
            <Button
              label={t('copyLink')}
              onClick={() => {
                const link = `${window.location.origin}/projects/${initialData.id}`
                navigator.clipboard
                  .writeText(link)
                  .then(() => toast.success(`${t('linkCopied')}`))
                  .catch(() => toast.error(`${t('failedToCopy')}`))
              }}
              startIcon={<img src={link} alt="error" />}
              variant="outlined"
              sx={{ color: 'white', borderColor: 'white' }}
              textTransform="uppercase"
            />
            <Box display="flex" gap={2}>
              <Button
                label={t('back')}
                variant="outlined"
                onClick={() => setAccessDialogOpen(false)}
                startIcon={<ChevronLeftIcon />}
                textTransform="uppercase"
              />
              <Button
                label={t('done')}
                variant="contained"
                onClick={handleShareProject}
                textTransform="uppercase"
              />
            </Box>
          </Box>
        }
        paperStyles={{ width: '800px' }}
        triggerElement={undefined}
      />

      <Dialog
        isOpen={confirmDialogOpen}
        onClose={() => {
          setConfirmDialogOpen(false)
          setPendingRoleChange(null)
        }}
        title={
          <Typography fontSize={20} fontWeight={500}>
            {t('changeRole')}
          </Typography>
        }
        content={
          <Typography fontSize={16} fontWeight={400} lineHeight="24px">
            <Trans
              i18nKey="project.shareProjectDialog.changeRoleDescription"
              ns="translation"
              values={{
                email: pendingRoleChange?.userEmail,
                oldRole: pendingRoleChange?.oldRole,
                newRole: pendingRoleChange?.newRole,
              }}
              components={{
                bold: <strong />,
                italic: <i />,
              }}
            />
          </Typography>
        }
        actionButtons={
          <Box display="flex" gap={2} justifyContent="flex-end">
            <Button
              label={t('cancel')}
              variant="outlined"
              onClick={() => {
                setConfirmDialogOpen(false)
                setPendingRoleChange(null)
              }}
              textTransform="uppercase"
            />
            <Button
              label={t('changeRole')}
              variant="contained"
              onClick={async () => {
                if (!pendingRoleChange) return

                try {
                  await updateAccess({
                    share_id: pendingRoleChange.userId,
                    role: pendingRoleChange.newRole,
                    caseId: initialData.id,
                  }).unwrap()
                  toast.success(t('roleUpdated'))
                  setConfirmDialogOpen(false)
                  setPendingRoleChange(null)
                  triggerGetSharedAccessUser(initialData.id)
                } catch (err) {
                  toast.error(t('failedToUpdateRole'))
                }
              }}
              textTransform="uppercase"
            />
          </Box>
        }
        triggerElement={undefined}
      />
    </>
  )
}

export default ShareProject
